<template>
  <div class="tianyi-requests-container">
    <div class="header">
      <h2>直连申请管理</h2>
      <div class="filters">
        <el-select v-model="filterStatus" placeholder="筛选状态" clearable style="width: 150px; margin-right: 10px;">
          <el-option label="待处理" value="pending"></el-option>
          <el-option label="处理中" value="processing"></el-option>
          <el-option label="已完成" value="completed"></el-option>
          <el-option label="失败" value="failed"></el-option>
        </el-select>
        <el-button type="primary" @click="fetchRequests">刷新</el-button>
      </div>
    </div>

    <!-- 批量操作 -->
    <div class="batch-actions" v-if="selectedIds.length > 0">
      <span>已选择 {{ selectedIds.length }} 项</span>
      <el-button type="danger" size="small" @click="batchDelete">批量删除</el-button>
    </div>

    <!-- 表格 -->
    <el-table
      :data="requests"
      stripe
      style="width: 100%"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="50"></el-table-column>
      <el-table-column prop="id" label="ID" width="80"></el-table-column>
      <el-table-column prop="title" label="游戏名称" min-width="250"></el-table-column>
      <el-table-column prop="targetId" label="魔趣ID" width="100"></el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">{{ getStatusLabel(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="upload_status" label="上传状态" width="100">
        <template slot-scope="scope">
          <span v-if="!scope.row.upload_status">-</span>
          <el-tag v-else :type="getUploadStatusType(scope.row.upload_status)">{{ scope.row.upload_status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="error_message" label="错误信息" min-width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.error_message" class="error-text">{{ scope.row.error_message }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="申请时间" width="180">
        <template slot-scope="scope">
          {{ formatTime(scope.row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.status === 'failed'"
            type="warning"
            size="small"
            @click="retryRequest(scope.row.id)"
          >
            重试
          </el-button>
          <el-button
            type="danger"
            size="small"
            @click="deleteRequest(scope.row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        @current-change="handlePageChange"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        layout="total, prev, pager, next, jumper"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { getTianyiDirectRequests, deleteTianyiDirectRequest, retryTianyiDirectRequest, batchDeleteTianyiDirectRequests } from '@/api'

export default {
  name: 'TianyiDirectRequests',
  data() {
    return {
      requests: [],
      currentPage: 1,
      pageSize: 20,
      total: 0,
      filterStatus: '',
      selectedIds: [],
      loading: false
    }
  },
  mounted() {
    this.fetchRequests()
  },
  methods: {
    async fetchRequests() {
      this.loading = true
      try {
        const res = await getTianyiDirectRequests({
          page: this.currentPage,
          pageSize: this.pageSize,
          status: this.filterStatus
        })
        
        if (res.code === 200) {
          this.requests = res.data.data
          this.total = res.data.pagination.total
        }
      } catch (err) {
        this.$message.error('获取申请列表失败')
      } finally {
        this.loading = false
      }
    },
    
    async deleteRequest(id) {
      this.$confirm('确定删除该申请吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await deleteTianyiDirectRequest(id)
          if (res.code === 200) {
            this.$message.success('删除成功')
            this.fetchRequests()
          }
        } catch (err) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    
    async retryRequest(id) {
      this.$confirm('确定重试该申请吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(async () => {
        try {
          const res = await retryTianyiDirectRequest(id)
          if (res.code === 200) {
            this.$message.success('已重置，将重新处理')
            this.fetchRequests()
          }
        } catch (err) {
          this.$message.error('重试失败')
        }
      }).catch(() => {})
    },
    
    async batchDelete() {
      this.$confirm(`确定删除选中的 ${this.selectedIds.length} 条申请吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await batchDeleteTianyiDirectRequests(this.selectedIds)
          if (res.code === 200) {
            this.$message.success(res.data.message)
            this.selectedIds = []
            this.fetchRequests()
          }
        } catch (err) {
          this.$message.error('批量删除失败')
        }
      }).catch(() => {})
    },
    
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.fetchRequests()
    },
    
    getStatusLabel(status) {
      const labels = {
        'pending': '待处理',
        'processing': '处理中',
        'completed': '已完成',
        'failed': '失败'
      }
      return labels[status] || status
    },
    
    getStatusType(status) {
      const types = {
        'pending': 'info',
        'processing': 'warning',
        'completed': 'success',
        'failed': 'danger'
      }
      return types[status] || 'info'
    },
    
    getUploadStatusType(status) {
      const types = {
        'uploading': 'warning',
        'completed': 'success',
        'failed': 'danger'
      }
      return types[status] || 'info'
    },
    
    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.tianyi-requests-container {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
}

.filters {
  display: flex;
  gap: 10px;
}

.batch-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f0f9ff;
  border-radius: 4px;
  margin-bottom: 10px;
}

.error-text {
  color: #f56c6c;
  font-size: 12px;
}

.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
</style>
